import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { RouteComponentProps } from '@reach/router';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-apollo';
import ConfirmDialog from '../../../components/ConfirmDialog';
import {
  createDiscountCode as createDiscountCodeQuery,
  deleteDiscountCode as deleteDiscountCodeQuery,
  loadDiscountCodes,
  updateDiscountCode as updateDiscountCodeQuery,
} from '../../../graphql/queries';
import {
  createDiscountCode as CreateDiscountCodeType,
  createDiscountCodeVariables,
  deleteDiscountCode as DeleteDiscountCodeType,
  deleteDiscountCodeVariables,
  DiscountCodes as DiscountCodesResponse,
  DiscountCodes_discountCodes,
  updateDiscountCode as UpdateDiscountCodeType,
  updateDiscountCodeVariables,
} from '../../../graphql/types';
import { getGraphqlErrorMessage } from '../../../lib/utils';

interface Props extends RouteComponentProps {}

// Stejné hodnoty, jaké povoluje server (odpovídají tabulce `discount`).
const DISCOUNT_PERCENTS = Array.from({ length: 16 }, (_, i) => (i + 1) * 5);
const CODE_PATTERN = /^[A-Z0-9_-]{3,32}$/;
// bez zaměnitelných znaků (0/O, 1/I)
const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

type FormState = {
  id: number | null;
  code: string;
  discount: number;
  validFrom: string;
  validTo: string;
  maxUses: string;
  active: boolean;
  note: string;
  locked: boolean;
};

const emptyForm: FormState = {
  id: null,
  code: '',
  discount: 20,
  validFrom: '',
  validTo: '',
  maxUses: '',
  active: true,
  note: '',
  locked: false,
};

const generateCode = () => {
  const bytes = new Uint8Array(8);
  window.crypto.getRandomValues(bytes);
  return Array.from(
    bytes,
    byte => CODE_ALPHABET[byte % CODE_ALPHABET.length],
  ).join('');
};

const formatDate = (date?: string | null) =>
  date ? moment(date).format('D. M. YYYY') : '–';

const getStatus = (code: DiscountCodes_discountCodes) => {
  const now = moment();
  if (!code.active) {
    return { label: 'Neaktivní', color: '#757575' };
  }
  if (code.validTo && moment(code.validTo).isBefore(now)) {
    return { label: 'Vypršel', color: '#c62828' };
  }
  if (code.validFrom && moment(code.validFrom).isAfter(now)) {
    return { label: 'Čeká na začátek', color: '#ef6c00' };
  }
  if (code.maxUses !== null && code.usedCount >= code.maxUses) {
    return { label: 'Vyčerpán', color: '#c62828' };
  }
  return { label: 'Platný', color: '#2e7d32' };
};

const DiscountCodes: React.FC<Props> = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [submitted, setSubmitted] = useState(false);
  const [
    deleteRow,
    setDeleteRow,
  ] = useState<DiscountCodes_discountCodes | null>(null);

  const onError = (err: any) =>
    enqueueSnackbar(
      err?.graphQLErrors?.[0]?.message || getGraphqlErrorMessage(err),
      {
        variant: 'error',
      },
    );

  const [createCode] = useMutation<
    CreateDiscountCodeType,
    createDiscountCodeVariables
  >(createDiscountCodeQuery, { onError });
  const [updateCode] = useMutation<
    UpdateDiscountCodeType,
    updateDiscountCodeVariables
  >(updateDiscountCodeQuery, { onError });
  const [deleteCode] = useMutation<
    DeleteDiscountCodeType,
    deleteDiscountCodeVariables
  >(deleteDiscountCodeQuery, { onError });

  const { data } = useQuery<DiscountCodesResponse>(loadDiscountCodes, {
    fetchPolicy: 'cache-and-network',
  });

  const openCreate = () => {
    setForm(emptyForm);
    setSubmitted(false);
    setDialogOpen(true);
  };

  const openEdit = (code: DiscountCodes_discountCodes) => {
    setForm({
      id: code.id,
      code: code.code,
      discount: code.discount,
      validFrom: code.validFrom
        ? moment(code.validFrom).format('YYYY-MM-DD')
        : '',
      validTo: code.validTo ? moment(code.validTo).format('YYYY-MM-DD') : '',
      maxUses: code.maxUses !== null ? String(code.maxUses) : '',
      active: code.active,
      note: code.note || '',
      locked: code.usedCount > 0,
    });
    setSubmitted(false);
    setDialogOpen(true);
  };

  const setField = <K extends keyof FormState>(name: K, value: FormState[K]) =>
    setForm(prev => ({ ...prev, [name]: value }));

  const handleGenerate = () => {
    setForm(prev => ({
      ...prev,
      code: generateCode(),
      // vygenerovaný kód je typicky pro jednoho člověka, lze přepsat
      maxUses: prev.maxUses.trim() ? prev.maxUses : '1',
    }));
  };

  const handleCopy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      enqueueSnackbar(`Kód ${code} zkopírován`, { variant: 'success' });
    } catch (e) {
      enqueueSnackbar('Kód se nepodařilo zkopírovat', { variant: 'error' });
    }
  };

  const normalizedCode = form.code.trim().toUpperCase();
  const maxUsesNumber =
    form.maxUses.trim() === '' ? null : Number(form.maxUses);
  const errors = {
    code: !CODE_PATTERN.test(normalizedCode)
      ? 'Kód musí mít 3 až 32 znaků (písmena bez diakritiky, čísla, - a _)'
      : '',
    maxUses:
      maxUsesNumber !== null &&
      (!Number.isInteger(maxUsesNumber) || maxUsesNumber < 1)
        ? 'Zadejte celé číslo od 1, nebo nechte prázdné pro neomezené použití'
        : '',
    dates:
      form.validFrom && form.validTo && form.validFrom > form.validTo
        ? 'Platnost "od" nesmí být později než "do"'
        : '',
  };
  const formValid = !errors.code && !errors.maxUses && !errors.dates;

  const handleSave = async () => {
    setSubmitted(true);
    if (!formValid) {
      return;
    }
    const input = {
      code: normalizedCode,
      discount: form.discount,
      validFrom: form.validFrom
        ? moment(form.validFrom, 'YYYY-MM-DD')
            .startOf('day')
            .toISOString()
        : null,
      validTo: form.validTo
        ? moment(form.validTo, 'YYYY-MM-DD')
            .endOf('day')
            .toISOString()
        : null,
      maxUses: maxUsesNumber,
      active: form.active,
      note: form.note.trim() || null,
    };
    const refetchQueries = [{ query: loadDiscountCodes }];
    const result =
      form.id === null
        ? await createCode({
            variables: { input },
            refetchQueries,
            awaitRefetchQueries: true,
          })
        : await updateCode({
            variables: { id: form.id, input },
            refetchQueries,
            awaitRefetchQueries: true,
          });
    if (result && result.data) {
      enqueueSnackbar(
        form.id === null ? 'Slevový kód vytvořen' : 'Slevový kód uložen',
        {
          variant: 'success',
        },
      );
      setDialogOpen(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteRow) {
      return;
    }
    const result = await deleteCode({
      variables: { id: deleteRow.id },
      refetchQueries: [{ query: loadDiscountCodes }],
      awaitRefetchQueries: true,
    });
    if (result && result.data) {
      enqueueSnackbar('Slevový kód smazán', { variant: 'success' });
    }
    setDeleteRow(null);
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Typography variant="h6">Slevové kódy</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={openCreate}
        >
          Přidat kód
        </Button>
      </div>
      <Typography
        variant="body2"
        color="textSecondary"
        style={{ marginBottom: 16 }}
      >
        Sleva se uplatňuje pouze na půjčovné, kauce se nesnižuje. Kód lze omezit
        časovou platností a/nebo počtem použití (jednorázový kód = 1 použití).
      </Typography>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Kód</TableCell>
            <TableCell>Sleva</TableCell>
            <TableCell>Platnost od</TableCell>
            <TableCell>Platnost do</TableCell>
            <TableCell>Použito</TableCell>
            <TableCell>Stav</TableCell>
            <TableCell>Poznámka</TableCell>
            <TableCell align="right">Akce</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data?.discountCodes.map(code => {
            const status = getStatus(code);
            return (
              <TableRow key={code.id}>
                <TableCell>
                  <strong>{code.code}</strong>
                  <Tooltip title="Zkopírovat kód">
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(code.code)}
                    >
                      <FileCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell>{code.discount} %</TableCell>
                <TableCell>{formatDate(code.validFrom)}</TableCell>
                <TableCell>{formatDate(code.validTo)}</TableCell>
                <TableCell>
                  {code.usedCount} /{' '}
                  {code.maxUses !== null ? code.maxUses : '∞'}
                </TableCell>
                <TableCell style={{ color: status.color, fontWeight: 500 }}>
                  {status.label}
                </TableCell>
                <TableCell>{code.note}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Upravit">
                    <IconButton size="small" onClick={() => openEdit(code)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Smazat">
                    <IconButton size="small" onClick={() => setDeleteRow(code)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
          {data && data.discountCodes.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} align="center">
                Zatím žádné slevové kódy
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {form.id === null ? 'Nový slevový kód' : 'Upravit slevový kód'}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Kód"
            fullWidth
            margin="normal"
            value={form.code}
            disabled={form.locked}
            error={submitted && !!errors.code}
            helperText={
              form.locked
                ? 'Kód už byl použit u objednávky, nelze měnit kód ani výši slevy.'
                : (submitted && errors.code) ||
                  'Např. CZSKI20. Zákazník ho zadává na webu.'
            }
            inputProps={{
              maxLength: 32,
              style: { textTransform: 'uppercase' },
            }}
            onChange={e => setField('code', e.target.value)}
            InputProps={{
              endAdornment: form.locked ? (
                undefined
              ) : (
                <InputAdornment position="end">
                  <Tooltip title="Vygenerovat unikátní kód (nastaví i jedno použití)">
                    <Button
                      size="small"
                      startIcon={<AutorenewIcon />}
                      onClick={handleGenerate}
                    >
                      Vygenerovat
                    </Button>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            label="Sleva z půjčovného"
            fullWidth
            margin="normal"
            value={form.discount}
            disabled={form.locked}
            onChange={e => setField('discount', Number(e.target.value))}
          >
            {DISCOUNT_PERCENTS.map(percent => (
              <MenuItem key={percent} value={percent}>
                {percent} %
              </MenuItem>
            ))}
          </TextField>
          <div style={{ display: 'flex', gap: 16 }}>
            <TextField
              label="Platnost od"
              type="date"
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
              value={form.validFrom}
              onChange={e => setField('validFrom', e.target.value)}
            />
            <TextField
              label="Platnost do (včetně)"
              type="date"
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
              value={form.validTo}
              error={submitted && !!errors.dates}
              helperText={submitted && errors.dates}
              onChange={e => setField('validTo', e.target.value)}
            />
          </div>
          <TextField
            label="Maximální počet použití"
            type="number"
            fullWidth
            margin="normal"
            value={form.maxUses}
            inputProps={{ min: 1 }}
            error={submitted && !!errors.maxUses}
            helperText={
              (submitted && errors.maxUses) ||
              'Prázdné = neomezeně. Jednorázový kód pro jednoho člověka: 1.'
            }
            onChange={e => setField('maxUses', e.target.value)}
          />
          <TextField
            label="Poznámka (jen pro interní potřebu)"
            fullWidth
            margin="normal"
            value={form.note}
            inputProps={{ maxLength: 255 }}
            onChange={e => setField('note', e.target.value)}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={form.active}
                color="primary"
                onChange={e => setField('active', e.target.checked)}
              />
            }
            label="Aktivní"
          />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="primary" onClick={handleSave}>
            Uložit
          </Button>
          <Button color="secondary" onClick={() => setDialogOpen(false)}>
            Zavřít
          </Button>
        </DialogActions>
      </Dialog>

      {deleteRow && (
        <ConfirmDialog
          open={true}
          onClose={() => setDeleteRow(null)}
          onAgree={handleDelete}
          titleLabel="Mazání slevového kódu"
          dialogContent={`Opravdu chcete smazat slevový kód ${deleteRow.code}? Použité kódy smazat nejde, ty stačí deaktivovat.`}
        />
      )}
    </div>
  );
};

export default DiscountCodes;
